/* eslint-disable no-param-reassign */
import { Injectable } from '@angular/core';

import { GameBase } from '../games/game-base';
import { CardModel, CardState, GameViewModel, HandState, SideState } from '../games/models/game-view.model';
import { CardValue } from '../models/communication/cards.enum';
import { CardsChanged } from '../models/communication/result-change.model';

/**
 * The service handles the state of the changed cards
 * and the game view model during the result change process.
 *
 * - It holds the reference to the game instance and modifies its view model as needed.
 * - It prepares/updates view model using the `open`, `close` and `confirm` methods.
 * - It contains the logic for `selecting`, `changing` and `reverting` the card values.
 */
@Injectable({
    providedIn: 'root'
})
export class ResultChange {
    selectedCard?: CardModel;

    private game!: GameBase;
    private originalData!: GameViewModel;
    private readonly changedCards = new Set<CardModel>();

    get canConfirm(): boolean {
        return this.changedCards.size > 0 && !this.selectedCard;
    }

    setGame(game: GameBase): void {
        this.game = game;
    }

    getOriginalData(): GameViewModel {
        return this.originalData;
    }

    // Service actions
    open(): void {
        this.changedCards.clear();
        this.originalData = this.game.cloneData();
        this.prepareStateOnOpen();

        this.game.updateTitle($localize`Select incorrect card`);
        this.game.onEnterShrinkMode();
    }

    confirm(): object {
        const result = this.encodeResult();
        this.updateCardValues();
        this.prepareStateOnClose();
        this.game.onExitShrinkMode();
        return result;
    }

    close(): void {
        this.revertCardValues();
        this.prepareStateOnClose();
        this.game.onExitShrinkMode();
    }

    // Cards actions
    selectCard(card: CardModel): void {
        if (this.selectedCard) {
            this.deselectIncorrect();
        } else {
            this.selectIncorrect(card);
        }
    }

    changeCardValue(newValue: CardValue): void {
        if (this.selectedCard) {
            this.selectedCard.value = newValue;
            this.selectedCard.isIncorrect = false;
            this.selectedCard.isCorrected = true;
            this.selectedCard.isInteractive = false;

            this.changedCards.add(this.selectedCard);
            this.toggleDisabled(false, { exclude: this.selectedCard });

            this.selectedCard = undefined;

            this.game.updateTitle($localize`Card corrected, confirm result or select next incorrect card`);
        }
    }

    revert(card: CardModel): void {
        card.value = this.originalData.cards[card.position].value;
        card.isCorrected = false;
        card.isInteractive = true;
        this.changedCards.delete(card);

        if (this.canConfirm) {
            this.game.updateTitle($localize`Card reverted, confirm result or select next incorrect card`);
        } else {
            this.game.updateTitle($localize`Select incorrect card`);
        }
    }

    revertCardValues(): void {
        if (this.changedCards.size > 0) {
            for (const card of this.originalData.cards) {
                this.game.view.cards[card.position].value = card.value;
            }
        }
    }

    // Private
    private selectIncorrect(card: CardModel): void {
        card.isIncorrect = true;
        this.selectedCard = card;

        this.toggleDisabled(true, { exclude: card });
        this.game.updateTitle($localize`Select correct card`);
    }

    private deselectIncorrect(): void {
        if (this.selectedCard) {
            this.selectedCard.isIncorrect = false;
            this.selectedCard = undefined;

            this.toggleDisabled(false);
            this.game.updateTitle($localize`Select incorrect card`);
        }
    }

    private prepareStateOnOpen(): void {
        for (const side of this.game.view.sides) {
            side.state = SideState.Default;
        }

        for (const hand of this.game.view.hands) {
            hand.state = HandState.Default;
        }

        for (const card of this.game.view.cards) {
            if (card.state === CardState.Show) {
                card.isInteractive = true;
            } else {
                card.isDisabled = true;
            }
        }
    }

    private prepareStateOnClose(): void {
        this.game.view.title = this.originalData.title;

        for (const [sideIndex, side] of this.game.view.sides.entries()) {
            side.state = this.originalData.sides[sideIndex].state;
        }

        for (const [handIndex, hand] of this.game.view.hands.entries()) {
            hand.state = this.originalData.hands[handIndex].state;
        }

        for (const [, card] of this.game.view.cards.entries()) {
            card.isInteractive = false;
            card.isIncorrect = false;
            card.isCorrected = false;
            card.isDisabled = false;
        }
    }

    private updateCardValues(): void {
        for (const card of Array.from(this.changedCards)) {
            this.game.view.cards[card.position].value = card.value;
        }
    }

    private encodeResult(): CardsChanged {
        return Array.from(this.changedCards).reduce((encoded, card) => {
            const oldValue = this.originalData.cards[card.position].value;
            return {
                ...encoded,
                [card.position]: { old: oldValue, new: card.value }
            };
        }, {});
    }

    private toggleDisabled(toggle: boolean, params: { exclude?: CardModel } = {}): void {
        for (const card of this.game.view.cards) {
            if (params.exclude !== card && card.state === CardState.Show) {
                card.isDisabled = toggle;
                card.isInteractive = !toggle;
            }
        }
    }
}
